// Демо-данные окна «Агенты искусственного интеллекта»: приветственный экран,
// галерея шаблонов и уже созданные агенты рабочего пространства Hub.
import { t } from "@/lib/i18n";

export type AgentTemplate = {
  id: string;
  emoji: string;
  icon: string;
  name: string;
  role: string;
  description: string;
  goal: string;
  instructions: string;
  tone: string;
  channels: string[];
};

export type AgentRecord = {
  id: string;
  name: string;
  emoji: string;
  role: string;
  template: string;
  goal: string;
  instructions: string;
  tone: string;
  channels: string[];
  enabled: boolean;
  handoff: boolean;
  dialogs: number;
  resolved: number;
};

// ── приветственный экран ──

export const agentIntro = {
  title: t("Агенты искусственного интеллекта"),
  subtitle: t("Обрабатывайте больше диалогов с меньшими усилиями, используя агентов искусственного интеллекта."),
  heading: t("Расширьте свою команду с помощью агентов искусственного интеллекта."),
  lead: t("Создан для обработки сотен диалогов одновременно."),
  cta: t("Начать"),
  more: t("Узнать больше"),
};

export const agentCapabilities = [
  {
    icon: "ri-flashlight-line",
    text: t("Мгновенные ответы на сообщения, круглосуточно, 7 дней в неделю."),
  },
  {
    icon: "ri-refresh-line",
    text: t("Автоматически обновляет этапы жизненного цикла и поля контактов."),
  },
  {
    icon: "ri-group-line",
    text: t("Назначает диалоги другим агентам, командам и агентам ИИ."),
  },
  {
    icon: "ri-book-open-line",
    text: t("Отвечает на вопросы, используя источники знаний, и многое другое!"),
  },
];

// превью диалога справа на приветственном экране
export const agentPreviewChat = [
  { id: 1, from: "client", text: "he Beginner included?", time: "10:12" },
  { id: 2, from: "client", text: "al 1 course includes live classes and is, twice a week. Can I get your email up please?", time: "10:12" },
  { id: 3, from: "client", text: "ail.com", time: "10:13" },
];

export const agentPreviewContact = {
  heading: t("Сведения о контакте"),
  initials: "AD",
  name: "Anna DiLaurentis",
  id: "49249458",
  fields: [
    { label: t("Телефон"), value: "+6012 345 6789" },
    { label: t("Электронная почта"), value: "anna4@gmail.com" },
  ],
};

// ── шаблоны ──

export const agentTemplatesTitle = t("Шаблоны AI-агентов");
export const agentTemplatesSubtitle =
  t("Используйте готового агента под задачу бизнеса — поддержка, продажи или приём заявок, — а можете собрать своего с нуля.");

export const agentTemplates: AgentTemplate[] = [
  {
    id: "receptionist",
    emoji: "🙋‍♀️",
    icon: "ri-customer-service-2-line",
    name: t("Администратор"),
    role: t("Приём обращений"),
    description:
      t("Приветствует клиентов, выясняет их потребности, фиксирует важные данные и аккуратно переводит диалог к нужному человеку или команде."),
    goal: t("Принять обращение, выяснить запрос и передать диалог нужному сотруднику."),
    instructions:
      t("Здоровайтесь по имени клиента, уточните город и задачу, зафиксируйте телефон и почту. Если вопрос вне вашей компетенции — передайте диалог ответственному и предупредите клиента, что с ним свяжутся."),
    tone: t("Дружелюбный"),
    channels: ["whatsapp", "telegram"],
  },
  {
    id: "sales",
    emoji: "💸",
    icon: "ri-line-chart-line",
    name: t("Агент продаж"),
    role: t("Квалификация лидов"),
    description:
      t("Приветствует потенциальных клиентов, узнаёт их потребности, предлагает подходящие продукты и подключает менеджера, когда клиент готов к разговору."),
    goal: t("Познакомиться с клиентом, выяснить задачу и довести до заявки или звонка менеджера."),
    instructions:
      t("Задайте 2–3 уточняющих вопроса о задаче и размере команды, предложите подходящий тариф и предложите демонстрацию. Не называйте цены, которых нет в источниках знаний."),
    tone: t("Деловой"),
    channels: ["whatsapp", "telegram", "email"],
  },
  {
    id: "support",
    emoji: "💎",
    icon: "ri-shield-star-line",
    name: t("Агент поддержки"),
    role: t("Ответы по продукту"),
    description:
      t("Отвечает на вопросы по продукту с помощью источников знаний AI и плавно передаёт диалог человеку, когда нужен специалист."),
    goal: t("Решить типовой вопрос клиента по продукту, не теряя контекста диалога."),
    instructions:
      t("Отвечайте только по источникам знаний. Если ответа нет или клиент просит человека — передайте диалог специалисту и опишите суть вопроса в заметке."),
    tone: t("Экспертный"),
    channels: ["whatsapp", "telegram", "email"],
  },
];

// ── параметры создания агента ──

export const agentChannels = [
  { id: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line" },
  { id: "telegram", label: "Telegram", icon: "ri-telegram-line" },
  { id: "email", label: t("Почта"), icon: "ri-mail-line" },
  { id: "instagram", label: "Instagram", icon: "ri-instagram-line" },
];

export const agentTones = [t("Дружелюбный"), t("Деловой"), t("Короткий и по делу"), t("Экспертный")];

// ── готовые агенты (появляются при включённых демо-данных) ──

export const demoAgents: AgentRecord[] = [
  {
    id: "agent-anna",
    name: t("Анна — приём обращений"),
    emoji: "🙋‍♀️",
    role: t("Администратор"),
    template: "receptionist",
    goal: t("Принять обращение, выяснить запрос и передать диалог нужному сотруднику."),
    instructions:
      t("Здоровайтесь по имени клиента, уточните город и задачу, зафиксируйте телефон и почту. Если вопрос вне вашей компетенции — передайте диалог ответственному."),
    tone: t("Дружелюбный"),
    channels: ["whatsapp", "telegram"],
    enabled: true,
    handoff: true,
    dialogs: 184,
    resolved: 141,
  },
  {
    id: "agent-lev",
    name: t("Лев — квалификация лидов"),
    emoji: "💸",
    role: t("Агент продаж"),
    template: "sales",
    goal: t("Познакомиться с клиентом, выяснить задачу и довести до заявки или звонка менеджера."),
    instructions:
      t("Задайте 2–3 уточняющих вопроса о задаче и размере команды, предложите подходящий тариф и предложите демонстрацию."),
    tone: t("Деловой"),
    channels: ["whatsapp", "telegram", "email"],
    enabled: true,
    handoff: true,
    dialogs: 96,
    resolved: 62,
  },
  {
    id: "agent-mira",
    name: t("Мира — поддержка"),
    emoji: "💎",
    role: t("Агент поддержки"),
    template: "support",
    goal: t("Решить типовой вопрос клиента по продукту, не теряя контекста диалога."),
    instructions:
      t("Отвечайте только по источникам знаний. Если ответа нет или клиент просит человека — передайте диалог специалисту."),
    tone: t("Экспертный"),
    channels: ["telegram", "email"],
    enabled: false,
    handoff: true,
    dialogs: 58,
    resolved: 47,
  },
];

export const agentHints = [
  {
    icon: "ri-shield-check-line",
    title: t("Что агент делает сам"),
    text: t("Отвечает по источникам знаний, задаёт уточняющие вопросы, ставит теги и двигает этап жизненного цикла."),
  },
  {
    icon: "ri-user-shared-line",
    title: t("Когда подключается человек"),
    text: t("Передача срабатывает по вашим правилам: клиент просит человека, вопрос вне источников или разговор слишком сложный."),
  },
  {
    icon: "ri-bar-chart-box-line",
    title: t("Как читать цифры"),
    text: t("«Диалогов» — сколько разговоров агент взял, «решено» — сколько закрыл без передачи сотруднику."),
  },
];